import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useInviteOrganizationMember,
  useOrganizationUserLookup,
} from "@/data/use-organizations-data";
import type { GetOrganizationUserLookupDto } from "@/lib/gen-api/types.gen";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { createLookupGeneration, settleLookup } from "./invite-lookup";

type InviteToOrganizationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationUuid: string;
};

export const InviteToOrganizationDialog = ({
  open,
  onOpenChange,
  organizationUuid,
}: InviteToOrganizationDialogProps) => {
  const { lookupUser, isLookingUp } =
    useOrganizationUserLookup(organizationUuid);
  const { inviteMember, isInviting } =
    useInviteOrganizationMember(organizationUuid);
  const [email, setEmail] = useState("");
  const [match, setMatch] = useState<GetOrganizationUserLookupDto | null>(null);
  const lookupGeneration = useRef(createLookupGeneration());

  useEffect(() => {
    lookupGeneration.current.cancel();
    setMatch(null);
  }, [organizationUuid]);

  const handleClose = () => {
    lookupGeneration.current.cancel();
    setEmail("");
    setMatch(null);
    onOpenChange(false);
  };

  const handleLookup = async (event: React.FormEvent) => {
    event.preventDefault();
    const token = lookupGeneration.current.begin();
    try {
      const user = await lookupUser(email);
      const settled = settleLookup({
        generation: lookupGeneration.current,
        token,
        result: { ok: true, value: user },
      });
      if (settled.apply) {
        setMatch(settled.apply);
      }
    } catch (error) {
      const settled = settleLookup({
        generation: lookupGeneration.current,
        token,
        result: { ok: false, error },
      });
      if (!settled.error) return;
      setMatch(null);
      toast.error(
        settled.error instanceof Error
          ? settled.error.message
          : "Failed to look up user.",
      );
    }
  };

  const handleInvite = async () => {
    if (!match) return;
    try {
      await inviteMember(match.uuid);
      toast.success(`Invited ${match.email}.`);
      handleClose();
    } catch {
      toast.error("Failed to invite member.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite to organization</DialogTitle>
          <DialogDescription>
            Invite an existing account. This does not add them to this
            workspace.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleLookup} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(event) => {
                lookupGeneration.current.cancel();
                setEmail(event.target.value);
                setMatch(null);
              }}
              placeholder="person@example.com"
              required
            />
          </div>
          {match && (
            <p className="text-sm">
              {match.name} ({match.email})
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            {!match && (
              <Button type="submit" disabled={isLookingUp || !email.trim()}>
                {isLookingUp ? "Looking up…" : "Look up"}
              </Button>
            )}
            {match && (
              <Button
                type="button"
                disabled={isInviting}
                onClick={() => void handleInvite()}
              >
                {isInviting ? "Inviting…" : "Invite"}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
